import IconButton from "@material-ui/core/IconButton";
// import DeleteIcon from "@material-ui/icons/Delete";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { PAGINATE_BY } from "@saleor/config";
import { configurationMenuUrl } from "@saleor/configuration";
import { Task } from "@saleor/containers/BackgroundTasks/types";
import useBackgroundTask from "@saleor/hooks/useBackgroundTask";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { maybe } from "@saleor/misc";
import SubscriberBulkDeleteDialog from "@saleor/subscribers/components/SubscriberBulkDeleteDialog";
import SubscriberExportDialog from "@saleor/subscribers/components/SubscriberExportDialog";
import SubscriberListPage from "@saleor/subscribers/components/SubscriberListPage";
import {
  useSubscriptionBulkDeleteMutation,
  useSubscriptionExport
} from "@saleor/subscribers/mutations";
import {
  useCountAllSubscriptions,
  useSubscriptionListQuery
} from "@saleor/subscribers/queries";
import {
  subscriberAddUrl,
  subscriberListUrl,
  SubscriberListUrlDialog,
  SubscriberListUrlQueryParams,
  subscriberUrl
} from "@saleor/subscribers/urls";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { useIntl } from "react-intl";

import useBulkActions from "../../../hooks/useBulkActions";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
import { getSortQueryVariables } from "./sort";

interface SubscriberListProps {
  params: SubscriberListUrlQueryParams;
}

const SubscriberList: React.FC<SubscriberListProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const shop = useShop();
  const { queue } = useBackgroundTask();

  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();

  const paginationState = createPaginationState(PAGINATE_BY, params);
  const filter = getFilterVariables(params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      sort: getSortQueryVariables(params)
    }),
    [params]
  );
  const { data, loading, refetch } = useSubscriptionListQuery({
    variables: queryVariables
  });

  const [
    subscriptionBulkDelete,
    subscriptionBulkDeleteOpts
  ] = useSubscriptionBulkDeleteMutation({
    onCompleted: data => {
      if (data.subscriptionBulkDelete.errors.length === 0) {
        closeModal();
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Subscriber successfully delete",
            description: "deleted multiple subscriber"
          })
        });
        reset();
        refetch();
      }
    }
  });
  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [openModal, closeModal] = createDialogActionHandlers<
    SubscriberListUrlDialog,
    SubscriberListUrlQueryParams
  >(navigate, subscriberListUrl, params);
  const countAllSubscriptions = useCountAllSubscriptions({});

  const [exportSubscriptions, exportSubscriptionsOpts] = useSubscriptionExport({
    onCompleted: data => {
      if (data.exportSubscriptions.errors.length === 0) {
        notify({
          text: intl.formatMessage({
            defaultMessage:
              "We are currently exporting your requested CSV. As soon as it is available it will be sent to your email address"
          }),
          title: intl.formatMessage({
            defaultMessage: "Exporting CSV",
            description: "waiting for export to end, header"
          })
        });
        queue(Task.EXPORT, {
          id: data.exportSubscriptions.exportFile.id
        });
        closeModal();
        reset();
      }
    }
  });

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    cleanupFn: reset,
    createUrl: subscriberListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      subscriberListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(subscriberListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.subscriptions.pageInfo),
    paginationState,
    params
  );

  const handleSort = createSortHandler(navigate, subscriberListUrl, params);
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");
  return (
    <>
      <SubscriberListPage
        subscribers={maybe(() =>
          data.subscriptions.edges.map(edge => edge.node)
        )}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        disabled={loading || subscriptionBulkDeleteOpts.loading}
        filterOpts={getFilterOpts(params)}
        initialSearch={params.query || ""}
        isChecked={isSelected}
        onAdd={() => navigate(subscriberAddUrl())}
        onAll={resetFilters}
        onExport={() => openModal("export")}
        onBack={() => navigate(configurationMenuUrl)}
        onFilterChange={changeFilters}
        onNextPage={loadNextPage}
        toatlCount = {maybe(() => data.subscriptions.totalCount,0)}
        onPreviousPage={loadPreviousPage}
        onRowClick={id => () => navigate(subscriberUrl(id))}
        onSearchChange={handleSearchChange}
        onSort={handleSort}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        pageInfo={pageInfo}
        selected={listElements.length}
        sort={getSortParams(params)}
        tabs={tabs.map(tab => tab.name)}
        toggle={toggle}
        toggleAll={toggleAll}
        toolbar={
          <IconButton
            color="primary"
            // onClick={() =>
            //   openModal("remove", {
            //     ids: listElements
            //   })
            // }
          >
            {/* <DeleteIcon /> */}
          </IconButton>
        }
      />
      <SubscriberBulkDeleteDialog
        confirmButtonState={subscriptionBulkDeleteOpts.status}
        open={params.action === "remove" && maybe(() => params.ids.length > 0)}
        onConfirm={() =>
          subscriptionBulkDelete({ variables: { ids: params.ids } })
        }
        onClose={closeModal}
        quantity={maybe(() => params.ids.length)}
      />
      <SubscriberExportDialog
        open={params.action === "export"}
        confirmButtonState={exportSubscriptionsOpts.status}
        errors={exportSubscriptionsOpts.data?.exportSubscriptions.errors || []}
        subscriberQuantity={{
          all: countAllSubscriptions.data?.subscriptions.totalCount,
          filter: data?.subscriptions.totalCount
        }}
        selectedSubscribers={listElements.length}
        onClose={closeModal}
        onSubmit={data =>
          exportSubscriptions({
            variables: {
              input: {
                ...data,
                filter,
                ids: listElements
              }
            }
          })
        }
      />
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
    </>
  );
};
export default SubscriberList;
